import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileWarning, ChevronRight, ShieldCheck } from "lucide-react";
import { api } from "../../lib/api";
import { useYacht } from "../../context/YachtContext";
import { PageHeader } from "./Dashboard";
import { Badge } from "../../components/ui/badge";

const STATUS_STYLE = {
  expired: "bg-red-100 text-red-700",
  critical: "bg-amber-100 text-amber-800",
  warning: "bg-yellow-100 text-yellow-800",
  notice: "bg-zinc-100 text-zinc-600",
};

const daysText = (d) =>
  d === null || d === undefined ? "" :
  d < 0 ? `expired ${-d}d ago` :
  d === 0 ? "expires today" : `${d}d left`;

// One screen for captains & above: every crew certificate and uploaded
// document (passport, IDs, contracts …) expiring soon across the yacht.
export default function CrewDocsOverview() {
  const { activeYacht } = useYacht();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    setData(null); setDenied(false);
    if (!activeYacht?.id) return;
    (async () => {
      try {
        const { data: d } = await api.get("/crew/cert-alerts", { params: { yacht_id: activeYacht.id } });
        setData(d);
      } catch (e) {
        if (e?.response?.status === 403) setDenied(true);
        else setData({ total: 0, counts: {}, alerts: [] });
      }
    })();
  }, [activeYacht?.id]);

  const grouped = useMemo(() => {
    const by = new Map();
    (data?.alerts || []).forEach((a) => {
      const k = a.user_id || "unknown";
      if (!by.has(k)) by.set(k, { user_id: a.user_id, name: a.name || "Crew member", items: [] });
      by.get(k).items.push(a);
    });
    return [...by.values()];
  }, [data]);

  if (!activeYacht) return null;
  if (denied) {
    return (
      <div className="p-6 lg:p-10 max-w-4xl">
        <PageHeader overline={activeYacht.name} title="Crew docs overview" />
        <p className="text-sm text-zinc-500 mt-4">Only senior crew can view crew document expiries.</p>
      </div>
    );
  }

  const counts = data?.counts || {};
  return (
    <div className="p-6 lg:p-10 max-w-4xl" data-testid="crew-docs-overview">
      <PageHeader
        overline={activeYacht.name}
        title="Crew docs overview"
        subtitle="Every crew certificate and document expiring soon on this yacht, most urgent first."
      />
      {data && (
        <div className="flex flex-wrap gap-2 mt-4 mb-6" data-testid="crew-docs-counts">
          {["expired", "critical", "warning", "notice"].map((s) =>
            counts[s] ? (
              <Badge key={s} className={`${STATUS_STYLE[s]} text-[11px]`} data-testid={`count-${s}`}>
                {counts[s]} {s}
              </Badge>
            ) : null,
          )}
        </div>
      )}
      {!data ? (
        <p className="text-sm text-zinc-400 mt-6">Loading…</p>
      ) : data.total === 0 ? (
        <div className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 p-6 flex items-center gap-3" data-testid="crew-docs-empty">
          <ShieldCheck className="h-6 w-6 text-emerald-600" />
          <div>
            <div className="font-medium text-emerald-900">All papers in order</div>
            <div className="text-sm text-emerald-800/80">No crew certificates or documents are expiring soon.</div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map((g) => (
            <div key={g.user_id} className="rounded-xl border border-zinc-200 bg-white" data-testid={`crew-docs-member-${g.user_id}`}>
              <Link
                to={`/app/crew-hr/${g.user_id}?yacht_id=${activeYacht.id}`}
                className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50 rounded-t-xl"
                data-testid={`crew-docs-open-${g.user_id}`}
              >
                <div className="font-medium text-sm">{g.name}</div>
                <div className="flex items-center gap-1 text-xs text-zinc-500">
                  Open HR record <ChevronRight className="h-3.5 w-3.5" />
                </div>
              </Link>
              <div className="divide-y divide-zinc-100">
                {g.items.map((a, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2 px-4 py-2.5 text-sm">
                    <FileWarning className={`h-4 w-4 shrink-0 ${a.status === "expired" ? "text-red-500" : "text-amber-500"}`} />
                    <span className="font-medium">{a.cert_label || a.cert_type}</span>
                    <Badge variant="outline" className="text-[10px]">
                      {a.source === "document" ? "Document" : "Certificate"}
                    </Badge>
                    <span className="text-zinc-500 text-xs">{(a.expiry_date || "").slice(0, 10)}</span>
                    <Badge className={`${STATUS_STYLE[a.status] || ""} text-[10px] ml-auto`}>
                      {daysText(a.days_left)}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
